import { CalendarDays, ChevronLeft, ChevronRight } from "lucide-react"
import { useMemo } from "react"

import type { DailyContentKind } from "@/core/daily-content/kinds"

import { Button } from "@/components/button"
import { CardHeading } from "@/components/shared/card-heading"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
} from "@/components/ui/card"
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible"
import {
  LabeledSelect,
  type LabeledSelectOption,
} from "@/components/ui/labeled-select"
import {
  dailyContentApi,
  type DailyContentProfile,
} from "@/lib/daily-content-api"
import {
  useCalendarDate,
  useCalendarView,
  useIsCalendarOpen,
  type DailyContentUiStore,
} from "@/lib/stores/daily-content-ui-store"

const CALENDAR_KIND_BADGE_CLASS: Record<DailyContentKind, string> = {
  question: "border-sky-300/45 bg-sky-300/15 text-sky-100",
  fact: "border-violet-300/45 bg-violet-300/15 text-violet-100",
}

const CALENDAR_VIEW_OPTIONS: readonly LabeledSelectOption<"month" | "week">[] =
  [
    { value: "month", label: "Month" },
    { value: "week", label: "Week" },
  ]

const DAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"]

type CalendarViewMode = "month" | "week"

type ParsedProfileSchedule = {
  profileId: string
  profileName: string
  kind: DailyContentKind
  hour: number
  minute: number
  allowedDays: Set<number> | null
}

type CalendarOccurrence = {
  profileId: string
  profileName: string
  kind: DailyContentKind
  dateKey: string
  timeLabel: string
}

type ScheduleCalendarProps = {
  profiles: DailyContentProfile[]
  store: DailyContentUiStore
}

export function ScheduleCalendar({ profiles, store }: ScheduleCalendarProps) {
  const calendarView = useCalendarView(store)
  const calendarDate = useCalendarDate(store)
  const isCalendarOpen = useIsCalendarOpen(store)

  const parsedSchedules = useMemo(() => {
    const parsed: ParsedProfileSchedule[] = []

    for (const profile of profiles) {
      const schedule = parseDailyCronSchedule(profile.scheduleValue)
      if (!schedule) {
        continue
      }

      parsed.push({
        profileId: profile.id,
        profileName: profile.name,
        kind: profile.kind,
        hour: schedule.hour,
        minute: schedule.minute,
        allowedDays: schedule.allowedDays,
      })
    }

    return parsed
  }, [profiles])

  const unsupportedCronCount = profiles.length - parsedSchedules.length

  const calendarRange = useMemo(() => {
    if (!isCalendarOpen) {
      const today = startOfDay(new Date())
      return { start: today, end: today }
    }

    if (calendarView === "week") {
      const start = startOfWeek(calendarDate)
      const end = addDays(start, 6)
      return { start, end }
    }

    const start = startOfMonth(calendarDate)
    const end = endOfMonth(calendarDate)
    return { start, end }
  }, [calendarDate, calendarView, isCalendarOpen])

  const calendarOccurrences = useMemo(() => {
    return buildOccurrencesForRange(
      parsedSchedules,
      calendarRange.start,
      calendarRange.end,
    )
  }, [calendarRange.end, calendarRange.start, parsedSchedules])

  const occurrencesByDateKey = useMemo(() => {
    const grouped = new Map<string, CalendarOccurrence[]>()
    for (const occurrence of calendarOccurrences) {
      const day = grouped.get(occurrence.dateKey) ?? []
      day.push(occurrence)
      grouped.set(occurrence.dateKey, day)
    }

    for (const [, day] of grouped) {
      day.sort((a, b) => a.timeLabel.localeCompare(b.timeLabel))
    }

    return grouped
  }, [calendarOccurrences])

  const monthGridDays = useMemo(() => {
    if (!isCalendarOpen || calendarView !== "month") {
      return []
    }

    const start = startOfWeek(startOfMonth(calendarDate))
    const end = endOfWeek(endOfMonth(calendarDate))
    const days: Date[] = []
    let cursor = start
    while (cursor.getTime() <= end.getTime()) {
      days.push(cursor)
      cursor = addDays(cursor, 1)
    }

    return days
  }, [calendarDate, calendarView, isCalendarOpen])

  const weekDays = useMemo(() => {
    if (!isCalendarOpen || calendarView !== "week") {
      return []
    }

    const start = startOfWeek(calendarDate)
    return Array.from({ length: 7 }, (_, index) => addDays(start, index))
  }, [calendarDate, calendarView, isCalendarOpen])

  const calendarTitle =
    calendarView === "month"
      ? calendarDate.toLocaleDateString(undefined, {
          month: "long",
          year: "numeric",
        })
      : `${calendarRange.start.toLocaleDateString(undefined, { month: "short", day: "numeric" })} - ${calendarRange.end.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}`

  return (
    <Card>
      <Collapsible
        open={isCalendarOpen}
        onOpenChange={(open) => {
          store.getState().setIsCalendarOpen(open)
        }}
      >
        <CardHeader>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <CardHeading
                icon={<CalendarDays className="size-4" />}
                className="inline-flex"
              >
                Schedule Calendar
              </CardHeading>
              <CardDescription>
                Visualize upcoming profile runs by week or month. Colors map to
                content kind.
              </CardDescription>
            </div>
            <CollapsibleTrigger className="inline-flex items-center gap-1.5 rounded-lg border border-border/70 bg-card/70 px-3 py-1.5 text-xs font-medium text-muted-foreground transition-colors hover:bg-card hover:text-foreground">
              {isCalendarOpen ? "Collapse" : "Expand"}
            </CollapsibleTrigger>
          </div>
        </CardHeader>
        <CollapsibleContent>
          <CardContent className="grid gap-4">
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-border/60 bg-card/40 p-3">
              <div className="inline-flex items-center gap-2">
                <Button
                  size="icon"
                  type="button"
                  variant="outline"
                  className="size-11 touch-manipulation md:size-9"
                  onClick={() => {
                    store
                      .getState()
                      .setCalendarDate(
                        calendarView === "month"
                          ? new Date(
                              calendarDate.getFullYear(),
                              calendarDate.getMonth() - 1,
                              1,
                            )
                          : addDays(calendarDate, -7),
                      )
                  }}
                >
                  <ChevronLeft className="size-4" />
                </Button>
                <Button
                  size="sm"
                  type="button"
                  variant="outline"
                  onClick={() =>
                    store.getState().setCalendarDate(startOfDay(new Date()))
                  }
                >
                  Today
                </Button>
                <Button
                  size="icon"
                  type="button"
                  variant="outline"
                  className="size-11 touch-manipulation md:size-9"
                  onClick={() => {
                    store
                      .getState()
                      .setCalendarDate(
                        calendarView === "month"
                          ? new Date(
                              calendarDate.getFullYear(),
                              calendarDate.getMonth() + 1,
                              1,
                            )
                          : addDays(calendarDate, 7),
                      )
                  }}
                >
                  <ChevronRight className="size-4" />
                </Button>
              </div>

              <div className="min-w-0 text-sm font-medium text-foreground">
                {calendarTitle}
              </div>

              <LabeledSelect
                value={calendarView}
                onValueChange={(view) => {
                  store.getState().setCalendarView(view)
                }}
                options={CALENDAR_VIEW_OPTIONS}
                label="View"
                wrapperClassName="min-w-32"
              />
            </div>

            <div className="grid gap-2 rounded-xl border border-border/60 bg-card/35 p-3 text-sm text-muted-foreground md:grid-cols-2">
              <div>
                Parsed {parsedSchedules.length} / {profiles.length} profiles
                into calendar events.
              </div>
              {unsupportedCronCount > 0 ? (
                <div>
                  {unsupportedCronCount} profile
                  {unsupportedCronCount === 1 ? " uses" : "s use"} advanced cron
                  patterns not shown in the calendar preview yet.
                </div>
              ) : (
                <div>All current profile schedules are visualized.</div>
              )}
            </div>

            {calendarView === "month" ? (
              <>
                <div className="grid gap-2 md:hidden">
                  {monthGridDays
                    .filter((day) => day.getMonth() === calendarDate.getMonth())
                    .map((day) => {
                      const dateKey = formatDateKey(day)
                      const dayItems = occurrencesByDateKey.get(dateKey) ?? []

                      return (
                        <div
                          key={`mobile-${dateKey}`}
                          className="rounded-xl border border-border/60 bg-card/55 p-3"
                        >
                          <div className="text-xs font-semibold text-muted-foreground">
                            {day.toLocaleDateString(undefined, {
                              weekday: "short",
                              month: "short",
                              day: "numeric",
                            })}
                          </div>
                          <div className="mt-2 grid gap-1.5">
                            {dayItems.length === 0 ? (
                              <div className="text-[0.68rem] text-muted-foreground">
                                No runs
                              </div>
                            ) : null}
                            {dayItems.map((item) => (
                              <div
                                key={`mobile-${item.profileId}-${item.timeLabel}`}
                                className={`rounded-md border px-2 py-1 text-[0.68rem]/4 ${CALENDAR_KIND_BADGE_CLASS[item.kind]}`}
                                title={`${item.profileName} at ${item.timeLabel} UTC`}
                              >
                                {item.timeLabel} {item.profileName}
                              </div>
                            ))}
                          </div>
                        </div>
                      )
                    })}
                </div>

                <div className="hidden grid-cols-7 gap-2 md:grid">
                  {DAY_LABELS.map((label) => (
                    <div
                      key={label}
                      className="text-kicker px-1 text-center text-xs font-semibold tracking-[0.14em] text-muted-foreground"
                    >
                      {label}
                    </div>
                  ))}
                  {monthGridDays.map((day) => {
                    const isCurrentMonth =
                      day.getMonth() === calendarDate.getMonth()
                    const dateKey = formatDateKey(day)
                    const dayItems = occurrencesByDateKey.get(dateKey) ?? []

                    return (
                      <div
                        key={dateKey}
                        className={`min-h-28 rounded-xl border p-2 ${isCurrentMonth ? "border-border/60 bg-card/55" : "border-border/35 bg-card/20"}`}
                      >
                        <div className="text-xs font-semibold text-muted-foreground">
                          {day.getDate()}
                        </div>
                        <div className="mt-1.5 grid gap-1">
                          {dayItems.slice(0, 3).map((item) => (
                            <div
                              key={`${item.profileId}-${item.timeLabel}`}
                              className={`rounded-md border px-2 py-1 text-[0.68rem]/4 ${CALENDAR_KIND_BADGE_CLASS[item.kind]}`}
                              title={`${item.profileName} at ${item.timeLabel} UTC`}
                            >
                              {item.timeLabel} {item.profileName}
                            </div>
                          ))}
                          {dayItems.length > 3 ? (
                            <div className="text-[0.68rem] text-muted-foreground">
                              +{dayItems.length - 3} more
                            </div>
                          ) : null}
                        </div>
                      </div>
                    )
                  })}
                </div>
              </>
            ) : (
              <div className="grid gap-2 md:grid-cols-7">
                {weekDays.map((day) => {
                  const dateKey = formatDateKey(day)
                  const dayItems = occurrencesByDateKey.get(dateKey) ?? []

                  return (
                    <div
                      key={dateKey}
                      className="min-h-44 rounded-xl border border-border/60 bg-card/55 p-2"
                    >
                      <div className="text-xs font-semibold text-muted-foreground">
                        {day.toLocaleDateString(undefined, {
                          weekday: "short",
                          month: "short",
                          day: "numeric",
                        })}
                      </div>
                      <div className="mt-2 grid gap-1.5">
                        {dayItems.length === 0 ? (
                          <div className="text-[0.68rem] text-muted-foreground">
                            No runs
                          </div>
                        ) : null}
                        {dayItems.map((item) => (
                          <div
                            key={`${item.profileId}-${item.timeLabel}`}
                            className={`rounded-md border px-2 py-1 text-[0.68rem]/4 ${CALENDAR_KIND_BADGE_CLASS[item.kind]}`}
                            title={`${item.profileName} at ${item.timeLabel} UTC`}
                          >
                            {item.timeLabel} {item.profileName}
                          </div>
                        ))}
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </CardContent>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  )
}

// Helper functions

const startOfDay = (value: Date): Date => {
  const next = new Date(value)
  next.setHours(0, 0, 0, 0)
  return next
}

const addDays = (value: Date, days: number): Date => {
  const next = new Date(value)
  next.setDate(next.getDate() + days)
  return startOfDay(next)
}

const startOfWeek = (value: Date): Date => {
  const date = startOfDay(value)
  return addDays(date, -date.getDay())
}

const endOfWeek = (value: Date): Date => {
  return addDays(startOfWeek(value), 6)
}

const startOfMonth = (value: Date): Date => {
  return new Date(value.getFullYear(), value.getMonth(), 1)
}

const endOfMonth = (value: Date): Date => {
  return new Date(value.getFullYear(), value.getMonth() + 1, 0)
}

const formatDateKey = (date: Date): string => {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, "0")
  const day = String(date.getDate()).padStart(2, "0")
  return `${year}-${month}-${day}`
}

const formatHourMinute = (hour: number, minute: number): string => {
  return `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`
}

const parseDayOfWeekField = (value: string): Set<number> | null => {
  if (value === "*") {
    return null
  }

  const parsed = new Set<number>()
  const parts = value.split(",").map((part) => part.trim())
  for (const part of parts) {
    if (!part) {
      return null
    }

    const asNumber = Number(part)
    if (!Number.isInteger(asNumber)) {
      return null
    }

    if (asNumber < 0 || asNumber > 7) {
      return null
    }

    parsed.add(asNumber === 7 ? 0 : asNumber)
  }

  return parsed
}

const parseDailyCronSchedule = (
  cron: string,
): { hour: number; minute: number; allowedDays: Set<number> | null } | null => {
  const fields = cron.trim().split(/\s+/)
  if (fields.length !== 5) {
    return null
  }

  const minuteField = fields[0]
  const hourField = fields[1]
  const dayOfMonthField = fields[2]
  const monthField = fields[3]
  const dayOfWeekField = fields[4]

  if (
    minuteField === undefined ||
    hourField === undefined ||
    dayOfMonthField === undefined ||
    monthField === undefined ||
    dayOfWeekField === undefined
  ) {
    return null
  }

  if (dayOfMonthField !== "*" || monthField !== "*") {
    return null
  }

  const minute = Number(minuteField)
  const hour = Number(hourField)
  if (
    !Number.isInteger(minute) ||
    !Number.isInteger(hour) ||
    minute < 0 ||
    minute > 59 ||
    hour < 0 ||
    hour > 23
  ) {
    return null
  }

  const allowedDays = parseDayOfWeekField(dayOfWeekField)
  if (dayOfWeekField !== "*" && allowedDays === null) {
    return null
  }

  return { hour, minute, allowedDays }
}

const buildOccurrencesForRange = (
  schedules: ParsedProfileSchedule[],
  start: Date,
  end: Date,
): CalendarOccurrence[] => {
  const output: CalendarOccurrence[] = []
  let cursor = startOfDay(start)

  while (cursor.getTime() <= end.getTime()) {
    const dayOfWeek = cursor.getDay()
    const dateKey = formatDateKey(cursor)

    for (const schedule of schedules) {
      if (schedule.allowedDays && !schedule.allowedDays.has(dayOfWeek)) {
        continue
      }

      output.push({
        profileId: schedule.profileId,
        profileName: schedule.profileName,
        kind: schedule.kind,
        dateKey,
        timeLabel: formatHourMinute(schedule.hour, schedule.minute),
      })
    }

    cursor = addDays(cursor, 1)
  }

  return output
}
